// src/components/ContentPage.js

import React, { useContext } from 'react';
import axios from 'axios';
import { LanguageContext } from '../contexts/LanguageContext';
import { useParams } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './ContentPage.css'; // Ensure this import is present for custom styles
// Removed: import translations from '../translations'; // Duplicate, use context instead
import { useQuery } from '@tanstack/react-query'; // Import useQuery from react-query
import { getContent } from '../services/apiService';

function ContentPage() {
  const { language, translations } = useContext(LanguageContext);
  const { slug } = useParams(); // e.g., "/how" => slug="how"

  // Helper function for translations
  const t = (key) => {
    return translations[key] || key;
  };

  // Use the modularized API call from the service module
  const fetchContent = () => getContent(slug);

  // Use react-query's useQuery hook with object syntax and customized retry logic
  const {
    data: pageData,
    error,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['content', slug, language],
    queryFn: fetchContent,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error)) {
        // Do not retry for 4xx client errors
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          return false;
        }
      }
      // Retry up to 2 times for other errors (e.g., network issues, 5xx server errors)
      return failureCount < 2;
    },
    onError: (err) => {
      console.error('Error fetching content:', err);
    },
  });

  const handleRetry = () => {
    refetch();
  };

  if (isError) {
    return (
      <div className="text-center mt-4">
        <Alert variant="danger">{t(errorMessage(error))}</Alert>
        {!isLoading && (
          <Button onClick={handleRetry} variant="primary">
            {t('retry')}
          </Button>
        )}
        {isLoading && (
          // Replace Spinner with customized Skeleton for retrying state
          <div className="d-flex justify-content-center align-items-center">
            <Skeleton height={40} width={100} className="custom-skeleton" />
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center mt-4">
        {/* Replace Spinner with customized Skeleton */}
        <Skeleton height={40} width={40} circle={true} className="custom-skeleton-circle" />
        <Skeleton count={3} className="custom-skeleton-text" />
      </div>
    );
  }

  // Render the content based on selected language, but DO NOT show `title`.
  const contentHtml =
    language === 'ka' ? pageData.content_ka : pageData.content_en;

  return (
    <div style={{ textAlign: 'left', margin: '1rem' }}>
      {/* Removed the <h2>{pageData.title}</h2> line */}
      <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
    </div>
  );
}

/**
 * Determines the appropriate error message based on the error object.
 * @param {object} error - The error object from react-query.
 * @returns {string} - The translation key for the error message.
 */
function errorMessage(error) {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      if (error.response.status === 404) {
        return 'contentNotFound';
      } else {
        return 'fetchError';
      }
    } else if (error.request) {
      return 'noServerResponse';
    } else {
      return 'requestSetupError';
    }
  }
  return 'fetchError';
}

export default ContentPage;
